import { HYDRATE } from "next-redux-wrapper";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { generateParamsFilters } from "@utils/generateParamsFilters";
import { API_ENDPOINTS } from "@shared/api/endpoints";
import { REQUEST_BODIES } from "@shared/api/requestBodies";
import {
    Brand,
    BrandSource,
    BrandsQueryArgs,
    CityOption,
    CityQueryArgs,
    CitySource,
    DisplacementsResponse,
    ElasticResponse,
    Generation,
    GenerationSource,
    GenerationsQueryArgs,
    Model,
    ModelSource,
    ModelsQueryArgs,
    OptionsResponse,
    optionsTags,
    QueryParams,
    SellerTypeSource,
    SellerTypesQueryArgs
} from "./types";

const ELASTIC_API = process.env.NEXT_PUBLIC_ELASTIC;
const API = process.env.NEXT_PUBLIC_API_URL;

export const filtersOptionsApi = createApi({
    reducerPath: "filtersOptionsApi",
    tagTypes: optionsTags,
    baseQuery: (args, api, extraOptions) => {
        const elasticEndpoints = [
            API_ENDPOINTS.BRANDS,
            API_ENDPOINTS.MODEL_SEARCH,
            API_ENDPOINTS.CITY_SEARCH,
            API_ENDPOINTS.GENERATIONS_SEARCH,
            API_ENDPOINTS.SELLER_TYPES_SEARCH
        ];
        const isElasticEndpoint = elasticEndpoints.some((endpoint) => args.url?.includes(endpoint));
        const baseUrl = isElasticEndpoint ? ELASTIC_API : API;
        const modifiedArgs = { ...args, url: `${baseUrl}${args.url}` };

        return fetchBaseQuery({
            baseUrl: "",
            headers: { "Content-Type": "application/json" }
        })(modifiedArgs, api, extraOptions);
    },
    extractRehydrationInfo(action, { reducerPath }) {
        if (action.type === HYDRATE) {
            return action.payload[reducerPath];
        }
    },
    endpoints: (builder) => ({
        getBrandsOptions: builder.query<Brand[], BrandsQueryArgs>({
            query: () => ({
                url: `${API_ENDPOINTS.BRANDS}`,
                method: "POST",
                body: REQUEST_BODIES.BRAND_OPTIONS_SORT
            }),
            transformResponse: (response: ElasticResponse<BrandSource>) => {
                const data = response?.data?.hits?.hits;
                const options = data?.map((item) => {
                    return {
                        id: item._source.id,
                        name: item._source.name,
                        alias: item._source.alias
                    };
                });
                return options || [];
            },
            providesTags: ["BRANDS_OPTIONS"]
        }),
        getModelsOptions: builder.query<Model[], ModelsQueryArgs>({
            query: (args) => ({
                url: `${API_ENDPOINTS.MODEL_SEARCH}`,
                method: "POST",
                body: REQUEST_BODIES.CREATE_MODELS_SEARCH_BODY(args.brandUuid)
            }),
            transformResponse: (response: ElasticResponse<ModelSource>) => {
                const data = response?.data?.hits?.hits;
                const options = data?.map((item) => {
                    return {
                        id: item._source.id,
                        name: item._source.name,
                        alias: item._source.alias
                    };
                });
                return options || [];
            },
            providesTags: ["MODELS_OPTIONS"]
        }),

        getCityOptions: builder.query<CityOption[], CityQueryArgs>({
            query: (args) => ({
                url: `${API_ENDPOINTS.CITY_SEARCH}`,
                method: "POST",
                body: args.exact
                    ? REQUEST_BODIES.CREATE_CITY_SEARCH_BY_NAME_BODY(args.cityName)
                    : REQUEST_BODIES.CREATE_CITY_SEARCH_BODY(args.cityName)
            }),
            transformResponse: (response: ElasticResponse<CitySource>) => {
                const data = response?.data?.hits?.hits;
                const options = data?.map((item) => {
                    return {
                        name: item._source.name,
                        alias: item._source.alias
                    };
                });
                return options || [];
            },
            providesTags: ["CITY_OPTIONS"]
        }),

        getBodyTypesOptions: builder.query<string[], QueryParams>({
            query: (args) => {
                const queryString = args ? generateParamsFilters(args) : "";
                return {
                    url: `${API_ENDPOINTS.BODY_TYPES(queryString)}`,
                    method: "GET"
                };
            },
            transformResponse: (response: OptionsResponse) => {
                return response.data.options.map((el) => el.name);
            },
            providesTags: ["BODY_TYPES_OPTIONS"]
        }),

        getTransmissionOptions: builder.query<string[], QueryParams>({
            query: (args) => {
                const queryString = generateParamsFilters(args);
                return {
                    url: `${API_ENDPOINTS.TRANSMISSION_TYPES(queryString)}`,
                    method: "GET"
                };
            },
            transformResponse: (response: OptionsResponse) => {
                return response.data?.options?.map((el) => el.name) ?? [];
            },
            providesTags: ["TRANSMISSION_OPTIONS"]
        }),

        getEngineTypesOptions: builder.query<string[], QueryParams>({
            query: (args) => {
                const queryString = generateParamsFilters(args);
                return {
                    url: `${API_ENDPOINTS.ENGINE_TYPES(queryString)}`,
                    method: "GET"
                };
            },
            transformResponse: (response: OptionsResponse) => {
                return response.data?.options?.map((el) => el.name) ?? [];
            },
            providesTags: ["ENGINE_OPTIONS"]
        }),

        getDriveTypesOptions: builder.query<string[], QueryParams>({
            query: (args) => {
                const queryString = generateParamsFilters(args);
                return {
                    url: `${API_ENDPOINTS.DRIVE_TYPES(queryString)}`,
                    method: "GET"
                };
            },
            transformResponse: (response: OptionsResponse) => {
                return response.data?.options?.map((el) => el.name) ?? [];
            },
            providesTags: ["DRIVE_TYPES_OPTIONS"]
        }),

        getGenerationsOptions: builder.query<Generation[], GenerationsQueryArgs>({
            query: (args) => ({
                url: `${API_ENDPOINTS.GENERATIONS_SEARCH}`,
                method: "POST",
                body: REQUEST_BODIES.CREATE_GENERATIONS_SEARCH_BODY(args.model_uuid)
            }),
            transformResponse: (response: ElasticResponse<GenerationSource>) => {
                const data = response?.data?.hits?.hits.map((item) => ({
                    id: item._source.id,
                    name: item._source.name,
                    generation: item._source.generation,
                    restyleNumber: item._source.restyle_number,
                    startedAt: item._source.started_at,
                    endedAt: item._source.ended_at
                }));
                return data || [];
            },
            providesTags: ["GENERATION_OPTIONS"]
        }),
        getDisplacementsOptions: builder.query<string[], QueryParams>({
            query: (args) => {
                const queryString = generateParamsFilters(args);
                return {
                    url: `${API_ENDPOINTS.DISPLACEMENTS(queryString)}`,
                    method: "GET"
                };
            },
            transformResponse: (response: DisplacementsResponse) => {
                let options = response.data?.options ?? [];
                options.sort((a, b) => a.name - b.name);
                return options.map((el) => el.name.toFixed(1));
            },
            providesTags: ["DISPLACEMENTS_OPTIONS"]
        }),
        getWheelsOptions: builder.query<string[], QueryParams>({
            query: (args) => {
                const queryString = generateParamsFilters(args);
                return {
                    url: `${API_ENDPOINTS.WHEEL_TYPES(queryString)}`,
                    method: "GET"
                };
            },
            transformResponse: (response: OptionsResponse) => {
                return response.data?.options?.map((el) => el.name) ?? [];
            },
            providesTags: ["WHEELS_OPTIONS"]
        }),
        getSellerTypesOptions: builder.query<string[], SellerTypesQueryArgs>({
            query: (args) => {
                const filters = args.brand && args.model ? {} : {};
                return {
                    url: `${API_ENDPOINTS.SELLER_TYPES_SEARCH}`,
                    method: "POST",
                    body: filters
                };
            },
            transformResponse: (response: ElasticResponse<SellerTypeSource>) => {
                const data = response.data?.hits?.hits;
                return data
                    ? Array.from(new Set(data.map((item) => item._source.seller_type)))
                    : [];
            },
            providesTags: ["SELLER_OPTIONS"]
        })
    })
});

export const {
    useGetBrandsOptionsQuery,
    useGetModelsOptionsQuery,
    useGetCityOptionsQuery,
    useGetBodyTypesOptionsQuery,
    useGetTransmissionOptionsQuery,
    useGetEngineTypesOptionsQuery,
    useGetDriveTypesOptionsQuery,
    useGetGenerationsOptionsQuery,
    useGetDisplacementsOptionsQuery,
    useGetWheelsOptionsQuery,
    useGetSellerTypesOptionsQuery,
    endpoints: {
        getBrandsOptions,
        getModelsOptions,
        getCityOptions,
        getBodyTypesOptions,
        getTransmissionOptions,
        getEngineTypesOptions,
        getDriveTypesOptions,
        getGenerationsOptions,
        getDisplacementsOptions,
        getWheelsOptions,
        getSellerTypesOptions
    }
} = filtersOptionsApi;